import { List } from "./list-utils";

export type SidebarContract = {
  id: string;
  propertyName: string | null;
  status: string | null;
  interestListId: string | null;
};

export type SidebarListEntry = {
  kind: "list";
  sortName: string;
  list: List;
  contractStatus: string | null;
};

export type SidebarOrphanSentEntry = {
  kind: "orphan-sent";
  sortName: string;
  contract: SidebarContract;
};

export type SidebarEntry = SidebarListEntry | SidebarOrphanSentEntry;

const normalizeStatus = (status: string | null | undefined): string =>
  String(status || "").toLowerCase();

const isSidebarContractStatus = (status: string | null | undefined): boolean => {
  const normalized = normalizeStatus(status);
  return normalized === "sent" || normalized === "pending" || normalized === "signed";
};

/** Contracts shown as orphan sidebar rows until the signed webhook links an interest list. */
const isOrphanableContractStatus = (status: string | null | undefined): boolean => {
  const normalized = normalizeStatus(status);
  return normalized === "sent" || normalized === "pending";
};

export const contractForList = (
  contracts: SidebarContract[],
  listId: string,
): SidebarContract | undefined =>
  contracts.find(
    (c) => isSidebarContractStatus(c.status) && c.interestListId === listId,
  );

/** @deprecated Use contractForList */
export const sentContractForList = contractForList;

export const buildSidebarEntries = (
  lists: List[],
  contracts: SidebarContract[],
): SidebarEntry[] => {
  const listIds = new Set(lists.map((l) => l.id));
  const sidebarContracts = contracts.filter((c) => isSidebarContractStatus(c.status));
  const orphanableContracts = sidebarContracts.filter((c) => isOrphanableContractStatus(c.status));

  const listEntries: SidebarListEntry[] = lists.map((list) => {
    const match = contractForList(sidebarContracts, list.id);
    return {
      kind: "list",
      sortName: list.name,
      list,
      contractStatus: match?.status ?? null,
    };
  });

  const orphanEntries: SidebarOrphanSentEntry[] = orphanableContracts
    .filter((c) => !c.interestListId || !listIds.has(c.interestListId))
    .map((contract) => ({
      kind: "orphan-sent",
      sortName: contract.propertyName || "Untitled Property",
      contract,
    }));

  return [...listEntries, ...orphanEntries].sort((a, b) =>
    a.sortName.localeCompare(b.sortName, undefined, { sensitivity: "base" }));
};

export type SidebarChip = {
  label: string;
  color: "info" | "warning";
};

/** Chip for a sent ("Awaiting signature") or pending ("Pending") contract. */
export const sidebarChipForContract = (
  contractStatus: string | null,
): SidebarChip | null => {
  const status = normalizeStatus(contractStatus);
  if (status === "pending") {
    return { label: "Pending", color: "info" };
  }
  if (status === "sent") {
    return { label: "Awaiting signature", color: "info" };
  }
  return null;
};

export const sidebarChipForList = (
  list: List,
  contractStatus: string | null,
): SidebarChip | null => {
  const contractChip = sidebarChipForContract(contractStatus);
  if (contractChip) {
    return contractChip;
  }
  if (list.property?.isDraft) {
    return { label: "Draft", color: "warning" };
  }
  return null;
};

/** Property settings header spells out the sidebar's terse "Draft" chip. */
export const propertySettingsHeaderChip = (
  list: List,
  contractStatus: string | null,
): SidebarChip | null => {
  const chip = sidebarChipForList(list, contractStatus);
  if (chip?.label === "Draft") {
    return { ...chip, label: "Draft Property" };
  }
  return chip;
};

export const listSidebarHref = (
  listId: string,
  contractStatus: string | null,
): string => {
  const status = normalizeStatus(contractStatus);
  if (status === "sent" || status === "pending") {
    return `/dash/manager/settings/property/${listId}/contract`;
  }
  return `/dash/manager/settings/property/${listId}/lists`;
};

/** Shown on every tab of a draft property's settings once its contract is signed. */
export const shouldShowImplementationFormNote = (
  isDraft: boolean,
  contractStatus: string | null,
): boolean => isDraft && normalizeStatus(contractStatus) === "signed";

export const defaultPropertySettingsPath = (
  listId: string,
  options: {
    contractStatus: string | null;
    hasContract: boolean;
  },
): string => {
  const { contractStatus, hasContract } = options;
  // Signed contracts land on Lists (HAV-190); every other status stays on the
  // Contract tab so onboarding isn't skipped for draft/null/sent/pending.
  if (!hasContract || normalizeStatus(contractStatus) === "signed") {
    return `/dash/manager/settings/property/${listId}/lists`;
  }
  return `/dash/manager/settings/property/${listId}/contract`;
};

/** Org interest lists are created when a contract is signed. */
export const shouldRefetchOrgListsOnContractUpdate = (
  contract: { status?: string | null } | null | undefined,
): boolean => normalizeStatus(contract?.status) === "signed";

export const propertyContractSettingsPath = (listId: string): string =>
  `/dash/manager/settings/property/${listId}/contract`;

/** Orphan sidebar contract pages redirect to the property contract tab once signed. */
export const shouldRedirectSignedOrphanContractToProperty = (
  contract: Pick<SidebarContract, "status" | "interestListId"> | null | undefined,
  options: { fromList: boolean },
): boolean => {
  if (options.fromList || !contract?.interestListId) {
    return false;
  }
  return normalizeStatus(contract.status) === "signed";
};
